import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { formatDateTimePK, formatDatePK } from '../utils';
import { CONFIGS } from '../config';

const AC_OPTIONS = [
  { val: '1', label: '1' },
  { val: '2', label: '2' },
  { val: '3', label: '3' },
  { val: '4', label: '4' },
  { val: 'none', label: 'Non-AC' },
];

// Brand colors lifted from the physical slip photo (navy title, orange
// "Booking Slip" pill, green/blue accent stripe).
const NAVY = '#1e3a5f';
const ORANGE = '#f97316';
const GREEN = '#22c55e';

const styles = StyleSheet.create({
  page: { padding: 28, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: '#ffffff', position: 'relative' },
  // Property photo behind everything, faded with a white overlay on top —
  // more reliable across @react-pdf/renderer versions than relying on the
  // Image component's own opacity support.
  bgImage: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' },
  bgOverlay: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#ffffff', opacity: 0.85 },

  headerRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
  logoCircle: {
    width: 52, height: 52, borderRadius: 26, borderWidth: 1.5, borderColor: NAVY,
    marginRight: 14, alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  },
  logoImg: { width: 52, height: 52, borderRadius: 26 },
  titleBlock: { flexGrow: 1 },
  title: { color: NAVY, fontSize: 22, fontFamily: 'Helvetica-Bold', letterSpacing: 0.5 },
  contactLine: { fontSize: 8, color: '#374151', marginTop: 2 },
  websiteLine: { fontSize: 8.5, color: ORANGE, fontFamily: 'Helvetica-Bold', marginTop: 2 },
  phoneCol: { alignItems: 'flex-end' },
  phoneLine: { fontSize: 8, color: '#374151', marginBottom: 1 },

  pillWrap: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  pillGroup: { flexDirection: 'row', gap: 8 },
  pill: { backgroundColor: ORANGE, color: '#ffffff', fontFamily: 'Helvetica-Bold', fontSize: 11, paddingVertical: 5, paddingHorizontal: 16, borderRadius: 14 },
  timestampText: { fontSize: 7.5, color: '#64748b' },

  formRow: { flexDirection: 'row', gap: 16 },
  col: { flex: 1 },
  field: { flexDirection: 'row', marginBottom: 10, borderBottomWidth: 1, borderBottomColor: '#94a3b8', paddingBottom: 3 },
  fieldLabel: { fontFamily: 'Helvetica-Bold', color: '#1e293b', width: 90 },
  fieldValue: { color: '#0f172a', flexGrow: 1 },
  signatureRow: { flexDirection: 'row', alignItems: 'flex-end', marginBottom: 10, borderBottomWidth: 1, borderBottomColor: '#94a3b8', paddingBottom: 3 },
  signatureImg: { width: 64, height: 46, objectFit: 'contain' },

  // Slot rows are deliberately plain-language (DAY / NIGHT / FULL) rather than
  // "10 Hours" / "22 Hours" so older or less tech-familiar guests can read the
  // slip at a glance; the actual slot is highlighted solid so it's unmistakable
  // even in a quick glance or a low-quality phone photo of the printed slip.
  slotRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8, padding: 6, borderRadius: 5, borderWidth: 1, borderColor: '#cbd5e1' },
  slotRowChecked: { backgroundColor: NAVY, borderColor: NAVY },
  // White box + a real tick-mark image for "checked" rather than a ✓ text
  // glyph — the standard PDF Helvetica font's WinAnsi encoding has no
  // checkmark glyph, so text-based ticks silently render blank. An image
  // needs no font support at all, and reads clearly against the navy row.
  slotCheckBox: { width: 16, height: 16, borderWidth: 1.5, borderColor: '#334155', marginRight: 10, alignItems: 'center', justifyContent: 'center' },
  slotCheckBoxChecked: { backgroundColor: '#ffffff', borderColor: '#ffffff' },
  slotTickImg: { width: 11, height: 11 },
  slotLabel: { fontFamily: 'Helvetica-Bold', fontSize: 10.5, color: '#1e293b' },
  slotLabelChecked: { color: '#ffffff' },
  slotSub: { fontSize: 8, color: '#64748b', marginTop: 1 },
  slotSubChecked: { color: '#e2e8f0' },

  // Compact inline checkbox row for AC count — reuses the same white-box +
  // tick-image language as the slot rows above, just smaller since there
  // are 5 options to fit on one line instead of 3 stacked rows.
  acRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10, borderBottomWidth: 1, borderBottomColor: '#94a3b8', paddingBottom: 4 },
  acOptions: { flexDirection: 'row', flexGrow: 1, flexWrap: 'wrap', gap: 7 },
  acOption: { flexDirection: 'row', alignItems: 'center' },
  acCheckBox: { width: 11, height: 11, borderWidth: 1.2, borderColor: '#334155', marginRight: 3, alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff' },
  acTickImg: { width: 8, height: 8 },
  acOptionLabel: { fontSize: 8, color: '#475569' },
  acOptionLabelChecked: { fontFamily: 'Helvetica-Bold', color: NAVY },

  verifiedStamp: {
    marginTop: 4, marginBottom: 10, alignSelf: 'flex-start',
    borderWidth: 1.5, borderColor: GREEN, borderRadius: 6,
    paddingVertical: 4, paddingHorizontal: 10,
  },
  verifiedStampText: { color: '#15803d', fontFamily: 'Helvetica-Bold', fontSize: 9 },

  paidStampImg: {
    width: 72, height: 72, marginTop: 6, alignSelf: 'flex-end',
    transform: 'rotate(-7deg)',
  },

  // Payment-deadline box (red while balance is still due, green once fully
  // paid) + the info/notes box beside it. Side by side rather than stacked
  // so both fit in the space left below the two-column form without needing
  // a taller page.
  // Fixed point widths rather than flexGrow/flexBasis percentages — Yoga
  // (react-pdf's layout engine) needs a width resolved up front for a Text
  // node to wrap against; a flexGrow-only box left the amber notes text
  // unwrapped and running straight off the page edge.
  noticeRow: { flexDirection: 'row', gap: 10, marginTop: 12 },
  noticeBox: { borderRadius: 6, borderWidth: 1, padding: 8 },
  redBox: { width: 138, backgroundColor: '#fee2e2', borderColor: '#ef4444' },
  greenBox: { width: 138, backgroundColor: '#dcfce7', borderColor: '#22c55e' },
  amberBox: { width: 216, backgroundColor: '#fef3c7', borderColor: '#f59e0b' },

  noticeTitleRed: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#991b1b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 },
  noticeDateRed: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#b91c1c' },
  noticeDayRed: { fontSize: 8, color: '#b91c1c', marginBottom: 5 },
  noticeNoteRed: { fontSize: 7.3, color: '#7f1d1d', lineHeight: 1.35 },

  noticeTitleGreen: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#166534', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 },
  noticeDateGreen: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#15803d', marginBottom: 5 },
  noticeNoteGreen: { fontSize: 7.3, color: '#14532d', lineHeight: 1.35 },

  noticeTitleAmber: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 5 },
  bulletRow: { flexDirection: 'row', marginBottom: 3 },
  bulletDot: { fontSize: 7.5, color: '#92400e', width: 9 },
  bulletText: { flexGrow: 1, fontSize: 7.3, color: '#78350f', lineHeight: 1.35 },

  // House rules — a separate, neutral-toned box (not red/amber like the
  // payment boxes above) since this is conduct/property policy, not a
  // payment deadline. Two columns so 6 short rules read at a glance
  // instead of one long vertical list.
  rulesBox: { marginTop: 10, width: 364, backgroundColor: '#eef2f7', borderWidth: 1, borderColor: '#94a3b8', borderRadius: 6, padding: 8 },
  rulesTitle: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: NAVY, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 },
  rulesGrid: { flexDirection: 'row', gap: 10 },
  rulesCol: { flex: 1 },
  ruleBulletRow: { flexDirection: 'row', marginBottom: 4 },
  ruleBulletDot: { fontSize: 7.5, color: NAVY, width: 9 },
  ruleBulletText: { flexGrow: 1, fontSize: 7.1, color: '#1e293b', lineHeight: 1.32 },

  footerStripe: { position: 'absolute', bottom: 0, left: 0, right: 0, height: 14, backgroundColor: GREEN },

  qrBox: {
    position: 'absolute', bottom: 30, right: 28,
    width: 100, borderWidth: 1, borderStyle: 'dashed', borderColor: '#334155',
    borderRadius: 6, padding: 8, alignItems: 'center',
  },
  qrLabel: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#475569', marginBottom: 4, textTransform: 'uppercase' },
  qrImg: { width: 80, height: 80 },
  qrCaption: { fontSize: 7, color: '#475569', marginTop: 4, textAlign: 'center' },

  // Large, mostly-opaque so a cancelled slip is unmistakable even glanced at
  // quickly or forwarded as a low-quality photo — centered over everything
  // else on the page, drawn last so nothing else can sit on top of it.
  cancelledStamp: {
    position: 'absolute', top: '50%', left: '50%',
    width: 320, height: 320 * (485 / 631), marginLeft: -160, marginTop: -(320 * (485 / 631)) / 2,
    opacity: 0.88,
  },
  // Staff signature drawn again here, on top of (after) the cancelled stamp
  // so it stays fully legible instead of being dimmed under the stamp like
  // the rest of the page — same spot as the normal signature field, just
  // re-drawn last so it's the topmost element.
  signatureOnStamp: {
    position: 'absolute', top: '50%', left: '50%',
    width: 150, height: 150 * (46 / 64), marginLeft: -75, marginTop: -(150 * (46 / 64)) / 2,
  },
});

function Field({ label, value }) {
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Text style={styles.fieldValue}>{value || ''}</Text>
    </View>
  );
}

function SlotRow({ label, sub, checked, tickImg }) {
  return (
    <View style={[styles.slotRow, checked && styles.slotRowChecked]}>
      <View style={[styles.slotCheckBox, checked && styles.slotCheckBoxChecked]}>
        {checked && tickImg && <Image src={tickImg} style={styles.slotTickImg} />}
      </View>
      <View>
        <Text style={[styles.slotLabel, checked && styles.slotLabelChecked]}>{label}</Text>
        <Text style={[styles.slotSub, checked && styles.slotSubChecked]}>{sub}</Text>
      </View>
    </View>
  );
}

// Pakistan local time regardless of the device/server's own timezone, so a
// slip generated from any machine always reads the same wall-clock time a
// customer in Karachi would recognize — same shared formatter the admin
// panel itself uses, so dates read identically everywhere in the app.
function formatBookingTimestamp(iso) {
  return `Slip generated on ${formatDateTimePK(iso || new Date().toISOString())} (PKT)`;
}

function AcRow({ value, tickImg }) {
  return (
    <View style={styles.acRow}>
      <Text style={styles.fieldLabel}>AC</Text>
      <View style={styles.acOptions}>
        {AC_OPTIONS.map(o => {
          const checked = value === o.val;
          return (
            <View key={o.val} style={styles.acOption}>
              <View style={styles.acCheckBox}>
                {checked && tickImg && <Image src={tickImg} style={styles.acTickImg} />}
              </View>
              <Text style={[styles.acOptionLabel, checked && styles.acOptionLabelChecked]}>{o.label}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function getDayName(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long' });
}

function getSlotFlags(slotType) {
  return {
    day: slotType === '10hour-day',
    night: slotType === '10hour-night',
    full: slotType === '22hour',
  };
}

// Same 3 bullets on every slip, except the payment-clearance one swaps to a
// thank-you line once there's no balance left — no point warning a
// fully-paid guest about a deadline that no longer applies to them.
function buildNoteBullets(hasBalance) {
  return [
    'PKR 10,000/- security deposit is paid at entry and refunded at exit, subject to no damage to property.',
    hasBalance
      ? 'Full payment must be cleared on or before your Last Date of Payment shown alongside.'
      : 'Thank you for clearing full payment in advance — see you at the farmhouse!',
    'Please bring a valid original CNIC matching the number on this slip for entry verification.',
  ];
}

export default function SlipDocument({ booking, qrDataUrl, logoDataUrl, backgroundDataUrl, signatureDataUrl, tickDataUrl, cancelledDataUrl, paidStampDataUrl, unitLabel }) {
  const { day, night, full } = getSlotFlags(booking.slotType);
  const isVerified = booking.status === 'confirmed' && booking.verifiedAt;
  // A paid-in-full receipt always reads as fully paid regardless of what's
  // actually stored on the booking record — this document's whole purpose
  // is to assert that, so it can't be generated in a half-paid state.
  const isPaidSlip = Boolean(paidStampDataUrl);
  const hasBalance = !isPaidSlip && Number(booking.balance) > 0;
  const noteBullets = buildNoteBullets(hasBalance);
  const maxPersonsLabel = CONFIGS[unitLabel === 'Silver' ? 'silver' : 'gold'].maxPersonsLabel;

  // The paid-slip variant adds a Guest Sign line + the round stamp under
  // it, which pushes the right column taller — but the QR box and footer
  // stripe are absolutely positioned relative to the *page* bottom, so
  // without extra page height they'd overlap the notice boxes below.
  const pageSize = isPaidSlip ? [420, 870] : [420, 790];

  return (
    <Document>
      <Page size={pageSize} style={styles.page}>
        {backgroundDataUrl && <Image src={backgroundDataUrl} style={styles.bgImage} />}
        {backgroundDataUrl && <View style={styles.bgOverlay} />}

        <View style={styles.headerRow}>
          <View style={styles.logoCircle}>
            {logoDataUrl && <Image src={logoDataUrl} style={styles.logoImg} />}
          </View>
          <View style={styles.titleBlock}>
            <Text style={styles.title}>RIAZ FARMHOUSE</Text>
            <Text style={styles.websiteLine}>riazfarmhouse.com</Text>
            <Text style={styles.contactLine}>riazfarmhouse@gmail.com</Text>
            <Text style={styles.contactLine}>Dua Green, Safari Resorts, Riaz FarmHouse, Near Abdullah Hotel, Karachi</Text>
          </View>
          <View style={styles.phoneCol}>
            <Text style={styles.phoneLine}>WhatsApp: 0300-2200883</Text>
            <Text style={styles.phoneLine}>WhatsApp: 0339-2959290</Text>
          </View>
        </View>

        <View style={styles.pillWrap}>
          <View style={styles.pillGroup}>
            <Text style={styles.pill}>Booking Slip</Text>
            {/* A gate pass is for an upcoming visit — a paid-in-full receipt
                is a closed-out record, so it's dropped on this variant. */}
            {!isPaidSlip && <Text style={styles.pill}>Gate Pass</Text>}
          </View>
          <Text style={styles.timestampText}>{formatBookingTimestamp(booking.createdAt)}</Text>
        </View>

        <View style={styles.formRow}>
          <View style={styles.col}>
            <Field label="S.No" value={booking.serialNo} />
            <Field label="Ms." value={booking.name} />
            <Field label="Contact No" value={booking.phone} />
            <Field label="NIC No" value={booking.nicNo} />
            <Field label="No of Person" value={booking.guestCount} />
            <Field label="Type" value={booking.bookingType === 'family' ? 'Family' : 'Friends'} />
            <SlotRow label="DAY SLOT (10 HRS)" sub="8 AM – 6 PM" checked={day} tickImg={tickDataUrl} />
            <SlotRow label="NIGHT SLOT (10 HRS)" sub="8 PM – 6 AM" checked={night} tickImg={tickDataUrl} />
            <SlotRow label="FULL DAY & NIGHT (22 HRS)" sub="8 AM – 6 AM" checked={full} tickImg={tickDataUrl} />
          </View>
          <View style={styles.col}>
            <Field label="Date" value={formatDatePK(booking.checkInDate)} />
            <Field label="Day" value={getDayName(booking.checkInDate)} />
            <Field label="Unit" value={unitLabel} />
            <AcRow value={booking.acCount} tickImg={tickDataUrl} />
            <Field label="Total" value={booking.totalPrice ? `Rs. ${Number(booking.totalPrice).toLocaleString()}` : ''} />
            <Field label="Advance" value={booking.advance ? `Rs. ${Number(booking.advance).toLocaleString()}` : ''} />
            <Field label="Balance" value={isPaidSlip ? 'Rs. 0 — Paid in Full' : (booking.balance ? `Rs. ${Number(booking.balance).toLocaleString()}` : '')} />
            <View style={styles.signatureRow}>
              <Text style={styles.fieldLabel}>Signature</Text>
              {signatureDataUrl && <Image src={signatureDataUrl} style={styles.signatureImg} />}
            </View>

            {isVerified && (
              <View style={styles.verifiedStamp}>
                <Text style={styles.verifiedStampText}>
                  Payment Verified {new Date(booking.verifiedAt).toLocaleDateString('en-GB')}
                </Text>
              </View>
            )}

            {/* Fully-paid slip only: a blank line for the guest to sign as
                acknowledgment, plus the round "FULLY PAID" stamp
                (public/fullypaid.png) right under it — medium-sized and
                gently rotated for a hand-stamped feel, not a full-page
                overlay like the cancelled stamp. */}
            {isPaidSlip && (
              <>
                <View style={styles.signatureRow}>
                  <Text style={styles.fieldLabel}>Guest Sign</Text>
                </View>
                <Image src={paidStampDataUrl} style={styles.paidStampImg} />
              </>
            )}
          </View>
        </View>

        <View style={styles.noticeRow}>
          <View style={[styles.noticeBox, hasBalance ? styles.redBox : styles.greenBox]}>
            {hasBalance ? (
              <>
                <Text style={styles.noticeTitleRed}>Last Date of Payment</Text>
                <Text style={styles.noticeDateRed}>{formatDatePK(booking.checkInDate)}</Text>
                <Text style={styles.noticeDayRed}>({getDayName(booking.checkInDate)}) — your visit date</Text>
                <Text style={styles.noticeNoteRed}>If the balance isn't fully paid by this date, your booking will be cancelled.</Text>
              </>
            ) : (
              <>
                <Text style={styles.noticeTitleGreen}>Payment Status</Text>
                <Text style={styles.noticeDateGreen}>Fully Paid</Text>
                <Text style={styles.noticeNoteGreen}>No balance due — thank you, see you at the farmhouse!</Text>
              </>
            )}
          </View>
          <View style={[styles.noticeBox, styles.amberBox]}>
            <Text style={styles.noticeTitleAmber}>Important Notes</Text>
            {noteBullets.map((n, i) => (
              <View style={styles.bulletRow} key={i}>
                <Text style={styles.bulletDot}>•</Text>
                <Text style={styles.bulletText}>{n}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.rulesBox}>
          <Text style={styles.rulesTitle}>House Rules &amp; Good to Know</Text>
          <View style={styles.rulesGrid}>
            <View style={styles.rulesCol}>
              <View style={styles.ruleBulletRow}>
                <Text style={styles.ruleBulletDot}>•</Text>
                <Text style={styles.ruleBulletText}>Max {maxPersonsLabel} guests (kids excluded from count). More than that? Extra charges apply — please confirm with us before booking.</Text>
              </View>
              <View style={styles.ruleBulletRow}>
                <Text style={styles.ruleBulletDot}>•</Text>
                <Text style={styles.ruleBulletText}>No alcohol, drugs, paan/gutka/mawa — zero tolerance.</Text>
              </View>
              <View style={styles.ruleBulletRow}>
                <Text style={styles.ruleBulletDot}>•</Text>
                <Text style={styles.ruleBulletText}>No fireworks, crackers, or gun firing.</Text>
              </View>
            </View>
            <View style={styles.rulesCol}>
              <View style={styles.ruleBulletRow}>
                <Text style={styles.ruleBulletDot}>•</Text>
                <Text style={styles.ruleBulletText}>24/7 CCTV surveillance across the property.</Text>
              </View>
              <View style={styles.ruleBulletRow}>
                <Text style={styles.ruleBulletDot}>•</Text>
                <Text style={styles.ruleBulletText}>Guest is financially liable for any damage to property.</Text>
              </View>
              <View style={styles.ruleBulletRow}>
                <Text style={styles.ruleBulletDot}>•</Text>
                <Text style={styles.ruleBulletText}>Please keep your belongings with you — Riaz Farmhouse is not responsible for loss or damage to personal items.</Text>
              </View>
            </View>
          </View>
        </View>

        {qrDataUrl && (
          <View style={styles.qrBox}>
            <Text style={styles.qrLabel}>QR Code</Text>
            <Image src={qrDataUrl} style={styles.qrImg} />
            <Text style={styles.qrCaption}>(scan to verify)</Text>
          </View>
        )}

        <View style={styles.footerStripe} />

        {cancelledDataUrl && <Image src={cancelledDataUrl} style={styles.cancelledStamp} />}
        {cancelledDataUrl && signatureDataUrl && <Image src={signatureDataUrl} style={styles.signatureOnStamp} />}
      </Page>
    </Document>
  );
}
